import {
  faAt,
  faBriefcase,
  faBrowser,
  faCalendarAlt,
  faCalendarDay,
  faCircle,
  faClock,
  faEllipsisH,
  faEnvelope,
  faEye,
  faFlag,
  faHistory,
  faIdCardAlt,
  faLandmark,
  faLocation,
  faPhone,
  faPhoneLaptop,
  faPortrait,
  faTag,
  faWifi,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import capitalize from 'lodash.capitalize';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IGeoIP, Person } from '@feathr/blackbox';
import {
  Fieldset,
  Form,
  FormElement,
  Input,
  List,
  ListItem,
  SaveButtonValid,
  Time,
} from '@feathr/components';
import TagsField from '@feathr/extender/components/TagsField';
import { useFlags } from '@feathr/extender/state';
import { flattenErrors, moment, TimeFormat } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import type { IPersonCompaniesSelectOption } from './PersonCompaniesSelect';
import PersonCompaniesSelect from './PersonCompaniesSelect';
import PersonCustomData from './PersonCustomData';

import * as styles from './PersonDetail.css';

interface IProps {
  person: Person;
}

function validatePersonInfo(person: Person): TValidateGrouped {
  return person.validate(['email'], false, 'grouped').errors;
}

function PersonInfo({ person }: IProps): JSX.Element {
  const flags = useFlags();
  const localStore = useLocalObservable(() => ({
    companies: [] as IPersonCompaniesSelectOption[],
    setCompanies(companies: IPersonCompaniesSelectOption[]): void {
      localStore.companies = companies;
    },
  }));
  const { t } = useTranslation();

  const validationErrors = validatePersonInfo(person);

  const sourceMap = {
    email_view: t('Email view'),
    email_link_click: t('Email link click'),
    page_view: t('Page view'),
    page_link_click: t('Page link click'),
    ad_view: t('Ad view'),
    ad_link_click: t('Ad link click'),
    unknown: t('Unknown'),
  };

  const geoip = person.get('geoip', {} as IGeoIP);

  function formatDate(date?: string): JSX.Element {
    if (date && moment.utc(date).isAfter('1970-01-01')) {
      return <Time format={TimeFormat.longDate} timestamp={date} />;
    }
    return <span>-</span>;
  }

  function handleChangeTags(tags: string[]): void {
    person.set({ tag_ids: tags });
  }

  return (
    <Form
      actions={
        <SaveButtonValid
          disabled={!person.isDirty}
          errors={flattenErrors(validationErrors)}
          method={'patch'}
          model={person}
        />
      }
      className={styles.info}
      label={t('Info')}
    >
      <Fieldset>
        <Input
          attribute={'external_id'}
          helpText={
            'An External ID is a unique identifier for a person record. This ID will be used to find the person for updates and deduplication.'
          }
          key={'external_id'}
          label={
            <>
              <FontAwesomeIcon icon={faIdCardAlt} /> {t('External ID')}
            </>
          }
          model={person}
          name={'external-id-input'}
          type={'text'}
        />

        <Input
          attribute={'name'}
          key={'name'}
          label={
            <>
              <FontAwesomeIcon icon={faPortrait} /> {t('Name')}
            </>
          }
          model={person}
          name={'name-input'}
          type={'text'}
        />

        <Input
          attribute={'first_name'}
          key={'first_name'}
          label={
            <>
              <FontAwesomeIcon icon={faPortrait} /> {t('First name')}
            </>
          }
          model={person}
          name={'first-name-input'}
          type={'text'}
        />

        <Input
          attribute={'last_name'}
          key={'last_name'}
          label={
            <>
              <FontAwesomeIcon icon={faPortrait} /> {t('Last name')}
            </>
          }
          model={person}
          name={'last-name-input'}
          type={'text'}
        />

        <Input
          attribute={'email'}
          key={'email'}
          label={
            <>
              <FontAwesomeIcon icon={faAt} /> {t('Email address')}
            </>
          }
          model={person}
          name={'email-address-input'}
          type={'text'}
        />
        <Input
          attribute={'phone'}
          key={'phone'}
          label={
            <>
              <FontAwesomeIcon icon={faPhone} /> {t('Phone number')}
            </>
          }
          model={person}
          name={'phone-number-input'}
          type={'text'}
        />
        <Input
          attribute={'occupation'}
          key={'occupation'}
          label={
            <>
              <FontAwesomeIcon icon={faBriefcase} /> {t('Title')}
            </>
          }
          model={person}
          name={'occupation-input'}
          type={'text'}
        />
        <PersonCompaniesSelect
          defaultOptions={localStore.companies}
          person={person}
          setCompanies={localStore.setCompanies}
        />
      </Fieldset>
      <FormElement key={'tags'}>
        <TagsField
          context={'person'}
          label={
            <>
              <FontAwesomeIcon icon={faTag} /> {t('Tags')}
            </>
          }
          onChange={handleChangeTags}
          value={person.get('tag_ids', [])}
        />
      </FormElement>
      <FormElement key={'custom'}>
        <PersonCustomData person={person} />
      </FormElement>
      <FormElement>
        <Fieldset label={t('Address data')}>
          <Input
            attribute={['address', 'premise1']}
            key={'premise1'}
            label={t('Street address line 1')}
            model={person}
            name={'street-address-line-1-input'}
            type={'text'}
          />
          <Input
            attribute={['address', 'premise2']}
            key={'premise2'}
            label={t('Street address line 2')}
            model={person}
            name={'street-address-line-2-input'}
            type={'text'}
          />
          <Input
            attribute={['address', 'locality']}
            key={'locality'}
            label={t('City')}
            model={person}
            name={'city-input'}
            type={'text'}
          />
          <Input
            attribute={['address', 'administrative_area_name']}
            key={'administrative_area_name'}
            label={t('State/Province')}
            model={person}
            name={'state-or-province-input'}
            type={'text'}
          />
          <Input
            attribute={['address', 'postal_code']}
            key={'postal_code'}
            label={t('ZIP/Postal code')}
            model={person}
            name={'zip-or-postal-code-input'}
            type={'text'}
          />
        </Fieldset>
      </FormElement>
      <Fieldset label={t('Tracked location data')}>
        <List>
          <ListItem className={styles.item} key={'geoip.latlng'}>
            <label>
              <FontAwesomeIcon icon={faLocation} /> {t('Approx. coord.')}
            </label>
            <span>
              {geoip.lat && geoip.lng
                ? t('{{latitude}}, {{longitude}}', { latitude: geoip.lat, longitude: geoip.lng })
                : t('Unknown')}
            </span>
          </ListItem>
          <ListItem className={styles.item} key={'geoip.postal_code'}>
            <label>
              <FontAwesomeIcon icon={faEnvelope} /> {t('ZIP/Postal code')}
            </label>
            <span>{geoip.postal_code || t('Unknown')}</span>
          </ListItem>
          <ListItem className={styles.item} key={'geoip.administrative_area_code'}>
            <label>
              <FontAwesomeIcon icon={faLandmark} /> {t('State/Province')}
            </label>
            <span>{geoip.administrative_area_code || t('Unknown')}</span>
          </ListItem>
          <ListItem className={styles.item} key={'geoip.country_code'}>
            <label>
              <FontAwesomeIcon icon={faFlag} /> {t('Country')}
            </label>
            <span>{geoip.country_code || t('Unknown')}</span>
          </ListItem>
          <ListItem className={styles.item} key={'geoip.timezone'}>
            <label>
              <FontAwesomeIcon icon={faClock} /> {t('Time zone')}
            </label>
            <span>{geoip.timezone || t('Unknown')}</span>
          </ListItem>
        </List>
      </Fieldset>
      <Fieldset label={t('Metadata')}>
        <List>
          <ListItem className={styles.item} key={'source'}>
            <label>
              <FontAwesomeIcon icon={faCircle} /> {t('Source')}
            </label>
            <span>
              {sourceMap[person.get('source', t('Unknown'))] ||
                capitalize(person.get('source', t('Unknown')))}
            </span>
          </ListItem>
          <ListItem className={styles.item} key={'placeholder'}>
            <label>
              <FontAwesomeIcon icon={faEllipsisH} /> {t('Placeholder')}
            </label>
            <span>{person.get('placeholder', { name: '-' }).name}</span>
          </ListItem>
          <ListItem className={styles.item} key={'date_created'}>
            <label>
              <FontAwesomeIcon icon={faCalendarDay} /> {t('First seen')}
            </label>
            {formatDate(person.get('date_created'))}
          </ListItem>
          <ListItem className={styles.item} key={'date_last_seen'}>
            <label>
              <FontAwesomeIcon icon={faCalendarDay} /> {t('Last seen')}
            </label>
            {formatDate(person.get('date_last_seen'))}
          </ListItem>
          <ListItem className={styles.item} key={'date_last_session_start'}>
            <label>
              <FontAwesomeIcon icon={faCalendarAlt} /> {t('Last session')}
            </label>
            {formatDate(person.get('date_last_session_start'))}
          </ListItem>
          <ListItem className={styles.item} key={'date_last_heard_from'}>
            <label>
              <FontAwesomeIcon icon={faCalendarDay} /> {t('Last heard from')}
            </label>
            {formatDate(person.get('date_last_heard_from'))}
          </ListItem>
          <ListItem className={styles.item} key={'last_brow'}>
            <label>
              <FontAwesomeIcon icon={faBrowser} /> {t('Browser')}
            </label>
            <span>
              {capitalize(
                person.get('last_brow') !== 'msie'
                  ? person.get('last_brow')
                  : t('Internet Explorer'),
              )}
            </span>
          </ListItem>
          <ListItem className={styles.item} key={'last_pform'}>
            <label>
              <FontAwesomeIcon icon={faPhoneLaptop} /> {t('Platform')}
            </label>
            <span>
              {{
                windows: t('Windows'),
                macos: t('OSX'),
                iphone: t('iPhone'),
                ipad: t('iPad'),
                android: t('Android'),
                chromeos: t('ChromeOS'),
              }[person.get('last_pform')] || capitalize(person.get('last_pform'))}
            </span>
          </ListItem>
          <ListItem className={styles.item} key={'seen_count'}>
            <label>
              <FontAwesomeIcon icon={faEye} /> {t('Times seen')}
            </label>
            <span>{person.get('seen_count', 0)}</span>
          </ListItem>
          <ListItem className={styles.item} key={'session_count'}>
            <label>
              <FontAwesomeIcon icon={faHistory} /> {t('Sessions')}
            </label>
            <span>{person.get('session_count', 0)}</span>
          </ListItem>
          {flags.GDPR_ipDisplay && (
            <ListItem className={styles.item} key={'last_ip'}>
              <label>
                <FontAwesomeIcon icon={faWifi} /> {t('IP address')}
              </label>
              <span>{person.get('last_ip', '-')}</span>
            </ListItem>
          )}
        </List>
      </Fieldset>
    </Form>
  );
}

export default observer(PersonInfo);
